import { useState } from 'react';
import { EMPTY_DOCTOR, EMPTY_MEDICATION } from '@shared/consumerPlan';
import { ApplicationSectionFrame } from '../components/application-section-frame';
import { PhoneField, TextField } from '../components/form-fields';
import { RepeatableEntries } from '../components/repeatable-entries';
import { useApplication } from '../hooks/use-application';
import type { ConsumerStepProps } from './step-props';

export function ApplicationMedicalStep({ clientId }: ConsumerStepProps) {
  const { answers } = useApplication(clientId);
  const [doctors, setDoctors] = useState(answers.doctors);
  const [medications, setMedications] = useState(answers.medications);

  return (
    <ApplicationSectionFrame
      clientId={clientId}
      section="medical"
      title="Your medical information"
      subtitle="The insurance company may ask your doctors for records. List the doctors you see and the medications you take regularly."
      draft={{ doctors, medications }}
    >
      <section aria-label="Your doctors" className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-gray-950">Your doctors</h2>
        <RepeatableEntries
          entries={doctors}
          onChange={setDoctors}
          createEntry={() => ({ ...EMPTY_DOCTOR })}
          entryTitle={(_, index) => (index === 0 ? 'Primary doctor' : `Doctor ${index + 1}`)}
          addLabel="Add another doctor"
          keepFirst
          renderEntry={(doctor, update) => (
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="Doctor’s name" value={doctor.name} onChange={name => update({ name })} />
              <TextField label="Practice or clinic" value={doctor.practice} onChange={practice => update({ practice })} />
              <PhoneField label="Phone" value={doctor.phone} onChange={phone => update({ phone })} />
              <TextField
                label="Last visit"
                type="month"
                value={doctor.lastVisit}
                onChange={lastVisit => update({ lastVisit })}
                hint="Month and year is enough."
              />
            </div>
          )}
        />
      </section>
      <section aria-label="Your medications" className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-gray-950">Your medications</h2>
        <p className="text-muted-foreground text-sm">Include prescriptions you take regularly. We’ve added the ones from your intake.</p>
        <RepeatableEntries
          entries={medications}
          onChange={setMedications}
          createEntry={() => ({ ...EMPTY_MEDICATION })}
          entryTitle={(medication, index) => medication.name || `Medication ${index + 1}`}
          addLabel="Add a medication"
          renderEntry={(medication, update) => (
            <div className="grid gap-4 sm:grid-cols-3">
              <TextField label="Medication" value={medication.name} onChange={name => update({ name })} />
              <TextField label="What it’s for" value={medication.reason} onChange={reason => update({ reason })} />
              <TextField label="Dose, if you know it" value={medication.dose} onChange={dose => update({ dose })} />
            </div>
          )}
        />
      </section>
    </ApplicationSectionFrame>
  );
}
